/**
 * The session status over the chat (Chris, 2026-10-30): one short line that
 * always says where the seeker is in the reading. It was the "In session with
 * {Mystic}" label, meant to be a status and never wired up; it now follows
 * what the app already knows (the server's conversation state, the reply in
 * flight, the picker, the reading being written, the talk's turns, a gift).
 *
 * Pure: the chat gathers the cues, this names them. Shown in caps by CSS.
 *
 *   sessionStatus({
 *     reader,        // the Mystic's name ('' → "The Mystic")
 *     instructions,  // the page's/server's own label (ChatInstructions)
 *     hidden,        // no label at all (the gift room's quiet pick)
 *     busy,          // a reply is out (the chat round trip or its stream)
 *     writing,       // ...and the Mystic's words have started to land
 *     picking, pickCount, picked,   // the card picker is up
 *     readingCards,  // the spread's reading is being written
 *     cardReading,   // a pulled card (talk, lesson) is being read
 *     painting,      // the reading's illustration is on its way
 *     talk,          // { text: '4 left' } while talking it through, else null
 *     giftFor,       // a reading for someone: their name
 *     giftFrom,      // the gift room: the sender's name
 *   }) → { text, busy }
 *
 * `busy` drives the shimmer: something is being made right now.
 */

const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

/** "four" for 4, digits past ten. */
export function countWord (n) {
  const k = Number(n)
  return Number.isInteger(k) && k >= 0 && k < WORDS.length ? WORDS[k] : String(n)
}

/** The old under-the-picker line ("Pick 4 cards from above."): never an idle label. */
export function isPickerLine (text) {
  return /^pick\b.*\bfrom above\.?$/i.test(String(text || '').trim())
}

const SESSION_WITH = /^in session with\s+(.+)$/i

// A name the server never sent ("undefined", "null") is no name.
const realName = (s) => { const t = String(s || '').trim(); return /^(undefined|null)$/i.test(t) ? '' : t }

function readerFrom ({ reader = '', instructions = '' } = {}) {
  const name = realName(reader)
  if (name) return name
  const m = String(instructions || '').trim().match(SESSION_WITH)
  return m ? realName(m[1]) : ''
}

function pickLabel ({ pickCount = 4, picked = 0 } = {}) {
  const count = Math.max(1, Number(pickCount) || 1)
  if (count === 1) return 'Choose your card'
  const done = Math.max(0, Math.min(count, Number(picked) || 0))
  const left = count - done
  if (done === 0) return `Choose your ${countWord(count)}`
  if (left <= 1) return 'Choose your last card'
  return `Choose ${left} more`
}

export function sessionStatus (cues = {}) {
  const c = cues || {}
  if (c.hidden) return { text: '', busy: false }
  const name = readerFrom(c)
  const who = name || 'The Mystic'
  const busy = (text) => ({ text, busy: true })
  const still = (text) => ({ text, busy: false })

  if (c.readingCards) return busy(`${who} is reading your cards…`)
  if (c.cardReading) return busy(`${who} is reading your card…`)
  if (c.painting) return busy('Painting your reading…')
  if (c.picking) {
    // The deck is being laid while the Mystic's line about it is still out.
    if (c.busy && !(Number(c.picked) > 0)) return busy('Shuffling the deck…')
    return still(pickLabel(c))
  }
  if (c.busy) return busy(c.writing ? `${who} is writing…` : `${who} is listening…`)
  if (c.talk) return still(c.talk.text ? `Talking it through · ${c.talk.text}` : 'Talking it through')
  if (c.giftFor) return still(`Reading for ${c.giftFor}`)
  if (c.giftFrom) return still(`A gift from ${c.giftFrom}`)
  const own = String(c.instructions || '').trim()
  if (own && !isPickerLine(own) && !SESSION_WITH.test(own)) return still(own)
  return still(name ? `In session with ${name}` : 'In session')
}
